import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { chatAPI, workspaceAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  MessageSquare, Plus, Search, Send, Users, User, Hash, Circle, Lock,
  MoreHorizontal, Trash2, UserPlus, X, ChevronLeft, Loader2, Megaphone,
  Paperclip, Mic, Square, Image as ImageIcon, FileText, Film, Reply,
  Download, MoreVertical,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getInitials(name) {
  return (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  if (isToday) return time;
  if (isYesterday) return `Yesterday ${time}`;
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}, ${time}`;
}

function groupMessagesByDate(messages) {
  const groups = [];
  let currentDate = '';
  for (const msg of messages) {
    const d = new Date(msg.created_at).toDateString();
    if (d !== currentDate) {
      currentDate = d;
      const today = new Date().toDateString();
      const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
      const dt = new Date(msg.created_at);
      let label = `${MONTHS[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
      if (d === today) label = 'Today';
      else if (d === yesterday.toDateString()) label = 'Yesterday';
      groups.push({ type: 'date', label });
    }
    groups.push({ type: 'message', data: msg });
  }
  return groups;
}

// Phase 3A Follow-up — channel-name validation matches the backend regex.
// Lowercase letters, digits, dashes; 2-32 chars; cannot start with a dash.
const CHANNEL_NAME_RE = /^[a-z0-9][a-z0-9-]{1,31}$/;

// Phase 3A Follow-up — render a message body containing structured mention
// tokens (`<@uuid>`) plus the literal broadcast tokens `@channel`/`@here`.
// `usersById` is a Map keyed by user_id; `selfId` is the viewer's user_id —
// self-mentions render with a stronger highlight (amber) while other users
// get a teal pill. `@channel` / `@here` render as violet pills.
function MessageBody({ text, usersById, selfId }) {
  if (!text) return null;
  // Tokenise: split on <@uuid>, @channel, @here while keeping the matches.
  const pattern = /(<@[a-f0-9-]{36}>|@channel\b|@here\b)/g;
  const parts = String(text).split(pattern);
  return (
    <>
      {parts.map((piece, i) => {
        if (!piece) return null;
        // Structured user mention.
        const m = /^<@([a-f0-9-]{36})>$/.exec(piece);
        if (m) {
          const uid = m[1];
          const u = usersById?.get(uid);
          const name = u?.name || u?.email?.split('@')[0] || 'user';
          const isSelf = uid === selfId;
          return (
            <span
              key={i}
              className={
                'inline-flex items-center px-1.5 py-0.5 mx-0.5 rounded text-[11.5px] font-semibold align-baseline ' +
                (isSelf
                  ? 'bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/30'
                  : 'bg-teal-500/20 text-teal-200 hover:underline cursor-pointer')
              }
              title={u?.email || ''}
              data-testid={isSelf ? 'mention-self' : 'mention-user'}
              data-mention-uid={uid}
            >
              @{name}
            </span>
          );
        }
        // Broadcast token.
        if (piece === '@channel' || piece === '@here') {
          return (
            <span
              key={i}
              className="inline-flex items-center px-1.5 py-0.5 mx-0.5 rounded text-[11.5px] font-semibold bg-violet-500/20 text-violet-200 align-baseline"
              data-testid={`mention-${piece.slice(1)}`}
            >
              {piece}
            </span>
          );
        }
        // Plain text — preserve line breaks via white-space:pre-wrap (set on parent).
        return <span key={i}>{piece}</span>;
      })}
    </>
  );
}

// =====================================================================
// D77 — Attachment + Reply renderers
// =====================================================================

// Build the auth-aware URL for a chat attachment. Backend serves from
// `/api/chat/{ws}/channels/{cid}/messages/{mid}/file` with auth, so we
// fetch then objectURL to avoid surfacing the bearer token in <img src>.
function useAuthedAttachmentUrl(workspaceId, channelId, msg) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!msg?.file_url || msg.deleted_for_all) { setUrl(''); return; }
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/chat/${workspaceId}/channels/${channelId}/messages/${msg.id}/file`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` } },
        );
        if (!res.ok) return;
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setUrl(objectUrl);
      } catch (_e) { /* swallow — show download button instead */ }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [workspaceId, channelId, msg?.id, msg?.file_url, msg?.deleted_for_all]);
  return url;
}

function MessageAttachment({ msg, workspaceId, channelId }) {
  const url = useAuthedAttachmentUrl(workspaceId, channelId, msg);
  if (!msg.file_url || msg.deleted_for_all) return null;
  if (msg.message_type === 'image') {
    return (
      <div className="mt-1 max-w-[340px]" data-testid={`chat-image-${msg.id}`}>
        {url ? (
          <img src={url} alt={msg.file_name || 'image'} className="rounded-lg max-h-[260px] object-contain bg-black/20" />
        ) : (
          <div className="h-[140px] flex items-center justify-center rounded-lg bg-foreground/[0.06]">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/40" />
          </div>
        )}
      </div>
    );
  }
  if (msg.message_type === 'voice') {
    return (
      <div className="mt-1" data-testid={`chat-voice-${msg.id}`}>
        {url ? (
          <audio controls src={url} className="h-9 max-w-[280px]" />
        ) : (
          <div className="inline-flex items-center gap-2 text-[11px] text-muted-foreground/60">
            <Loader2 className="h-3 w-3 animate-spin" /> loading voice…
          </div>
        )}
      </div>
    );
  }
  if (msg.message_type === 'video') {
    return (
      <div className="mt-1 max-w-[360px]" data-testid={`chat-video-${msg.id}`}>
        {url ? (
          <video controls src={url} className="rounded-lg max-h-[320px]" preload="metadata" />
        ) : (
          <div className="h-[140px] flex items-center justify-center rounded-lg bg-foreground/[0.06]">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/40" />
          </div>
        )}
      </div>
    );
  }
  // document fallback
  return (
    <a
      href={url || '#'}
      download={msg.file_name || 'attachment'}
      className="mt-1 inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-foreground/[0.04] text-[12px] hover:bg-foreground/[0.07]"
      data-testid={`chat-doc-${msg.id}`}
    >
      <FileText className="h-3.5 w-3.5 text-muted-foreground/70" />
      <span className="truncate max-w-[200px]">{msg.file_name || 'File'}</span>
      <Download className="h-3 w-3 text-muted-foreground/60" />
    </a>
  );
}

function ReplyPreview({ preview }) {
  if (!preview) return null;
  return (
    <div
      className="mb-1 pl-2 border-l-2 border-gold/40 text-[11px] text-muted-foreground/70 truncate"
      data-testid="reply-preview"
    >
      <div className="font-medium text-gold/80 truncate">{preview.sender_name}</div>
      <div className="truncate">{preview.text || '…'}</div>
    </div>
  );
}



export default function TeamChatPage() {
  const { user } = useAuth();
  const { currentWorkspace, workspaceRole } = useWorkspace();
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatType, setNewChatType] = useState('dm');
  const [newChatName, setNewChatName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState({});
  const [showMobile, setShowMobile] = useState(false);
  const [channelSearch, setChannelSearch] = useState('');
  // Phase 3A Follow-up — Named-channel modal state (separate from DM modal).
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelDesc, setNewChannelDesc] = useState('');
  const [newChannelType, setNewChannelType] = useState('public'); // public | private
  const [newChannelMembers, setNewChannelMembers] = useState([]);
  const [newChannelSubmitting, setNewChannelSubmitting] = useState(false);
  // Delete-channel confirmation
  const [deleteChannelTarget, setDeleteChannelTarget] = useState(null);
  // @-autocomplete state for composer
  const [mentionQuery, setMentionQuery] = useState(null); // {start, query} | null
  const [mentionSelectedIdx, setMentionSelectedIdx] = useState(0);
  // user_ids structurally mentioned in the current draft (cleared on send)
  const [draftMentionIds, setDraftMentionIds] = useState([]);
  // D77 — Reply target, recording state, upload progress, delete modal target
  const [replyTo, setReplyTo] = useState(null);                  // {id, sender_name, text, message_type}
  const [recording, setRecording] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);         // message obj
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recorderChunksRef = useRef([]);
  const composerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const wsId = currentWorkspace?.id;
  // Owner/Admin (or super_admin globally) can create + delete channels.
  const isElevated =
    user?.role === 'super_admin' ||
    workspaceRole === 'owner' ||
    workspaceRole === 'admin';
  const isOwner = user?.role === 'super_admin' || workspaceRole === 'owner';
  // Owner/Admin/Manager can use @channel broadcast.
  const canBroadcast =
    user?.role === 'super_admin' ||
    workspaceRole === 'owner' ||
    workspaceRole === 'admin' ||
    workspaceRole === 'manager';

  // Lookup map for mention pill rendering (user_id → user).
  const usersById = useMemo(() => {
    const m = new Map();
    (members || []).forEach((u) => u?.id && m.set(u.id, u));
    if (user?.id) m.set(user.id, user);
    return m;
  }, [members, user]);

  // Fetch channels
  const fetchChannels = useCallback(async () => {
    if (!wsId) return;
    try {
      const res = await chatAPI.channels(wsId);
      setChannels(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [wsId]);

  // Fetch workspace members — try admin endpoint first, fall back to lite
  // roster for non-admin viewers (Team / Manager users still need names for
  // mention pills and @-autocomplete).
  useEffect(() => {
    if (!wsId) return;
    workspaceAPI.listMembers(wsId)
      .then(res => {
        const m = (res.data || []).map(item => item.user).filter(Boolean);
        setMembers(m);
      })
      .catch(() => {
        workspaceAPI.listMembersLite(wsId)
          .then(res => setMembers(res.data || []))
          .catch(() => setMembers([]));
      });
  }, [wsId]);

  useEffect(() => { fetchChannels(); }, [fetchChannels]);

  // Poll for online users
  useEffect(() => {
    if (!wsId) return;
    const poll = () => chatAPI.online(wsId).then(res => setOnlineUsers(new Set(res.data || []))).catch(() => {});
    poll();
    const iv = setInterval(poll, 15000);
    return () => clearInterval(iv);
  }, [wsId]);

  // Fetch messages for active channel
  const fetchMessages = useCallback(async () => {
    if (!wsId || !activeChannel) return;
    setMsgLoading(true);
    try {
      const res = await chatAPI.messages(wsId, activeChannel.id);
      setMessages(res.data || []);
      chatAPI.markRead(wsId, activeChannel.id).catch(() => {});
      // Update unread in channel list
      setChannels(prev => prev.map(ch => ch.id === activeChannel.id ? { ...ch, unread_count: 0 } : ch));
      // Batch 2A CHAT-USER-002 / T-018 — broadcast to the shell so the
      // sidebar unread badge clears immediately (without waiting for the
      // 20-second poll cycle).
      try { window.dispatchEvent(new CustomEvent('chat-channel-read', { detail: { channelId: activeChannel.id } })); } catch {}
    } catch { /* ignore */ }
    setMsgLoading(false);
  }, [wsId, activeChannel]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // WebSocket connection with polling fallback
  useEffect(() => {
    if (!activeChannel || !wsId) return;
    const token = localStorage.getItem('stellar_token');
    if (!token) return;

    let ws = null;
    let wsConnected = false;

    // Try WebSocket
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    try {
      ws = new WebSocket(`${wsUrl}/api/ws/chat/${activeChannel.id}?token=${token}`);
      ws.onopen = () => { wsConnected = true; };
      ws.onmessage = (evt) => {
        const data = JSON.parse(evt.data);
        if (data.type === 'new_message') {
          setMessages(prev => {
            if (prev.some(m => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          });
          // Mark read immediately
          if (data.message.sender_id !== user?.id) {
            chatAPI.markRead(wsId, activeChannel.id).catch(() => {});
          }
          // Update channel list
          setChannels(prev => prev.map(ch =>
            ch.id === activeChannel.id
              ? { ...ch, last_message: { text: data.message.text, sender_name: data.message.sender_name, timestamp: data.message.created_at }, unread_count: 0 }
              : ch
          ));
        } else if (data.type === 'typing' && data.user_id !== user?.id) {
          setTypingUsers(prev => ({ ...prev, [data.user_id]: data.user_name }));
          setTimeout(() => setTypingUsers(prev => { const n = { ...prev }; delete n[data.user_id]; return n; }), 3000);
        } else if (data.type === 'user_online') {
          setOnlineUsers(prev => new Set([...prev, data.user_id]));
        } else if (data.type === 'user_offline') {
          setOnlineUsers(prev => { const n = new Set(prev); n.delete(data.user_id); return n; });
        }
      };
      ws.onerror = () => { wsConnected = false; };
      ws.onclose = () => { wsConnected = false; };
      wsRef.current = ws;
    } catch { wsConnected = false; }

    // Polling fallback (every 3s)
    pollRef.current = setInterval(async () => {
      if (wsConnected) return;
      try {
        const res = await chatAPI.messages(wsId, activeChannel.id);
        setMessages(res.data || []);
      } catch { /* ignore */ }
    }, 3000);

    return () => {
      if (ws) { try { ws.close(); } catch {} }
      if (pollRef.current) clearInterval(pollRef.current);
      wsRef.current = null;
    };
  }, [activeChannel?.id, wsId, user?.id]);

  // Poll channels for unread badges
  useEffect(() => {
    if (!wsId) return;
    const iv = setInterval(fetchChannels, 10000);
    return () => clearInterval(iv);
  }, [wsId, fetchChannels]);

  const sendMessage = async () => {
    if (!msgText.trim() || !activeChannel || !wsId) return;
    const text = msgText.trim();
    // Phase 3A Follow-up — extract mention payload from the draft.
    const structuredIds = Array.from(text.matchAll(/<@([a-f0-9-]{36})>/g)).map((m) => m[1]);
    const mentionUserIds = Array.from(new Set(structuredIds.filter((uid) => draftMentionIds.includes(uid))));
    const mentionChannel = /(^|\s)@channel(\s|$)/.test(text);
    const mentionHere = /(^|\s)@here(\s|$)/.test(text);
    if ((mentionChannel || mentionHere) && !canBroadcast) {
      toast.warning('Broadcast restricted', {
        description: "Only Owner, Admin, and Manager can notify the whole channel. Your message will send but won't ping everyone.",
        duration: 5000,
      });
    }
    setMsgText('');
    setDraftMentionIds([]);
    // D77 — capture and clear the reply target before the async send so a
    // fast 2nd send doesn't accidentally inherit the previous reply.
    const replyId = replyTo?.id || null;
    setReplyTo(null);
    const payload = {
      text,
      mention_user_ids: mentionUserIds,
      mention_channel: mentionChannel,
      mention_here: mentionHere,
      reply_to_message_id: replyId,
    };
    // Reply-aware: prefer the REST path (which writes reply_preview) when
    // we're replying — WS path doesn't carry the reply field today.
    if (!replyId && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'message', ...payload }));
    } else {
      try {
        const res = await chatAPI.sendMessage(wsId, activeChannel.id, payload);
        setMessages(prev => {
          if (prev.some(m => m.id === res.data.id)) return prev;
          return [...prev, res.data];
        });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(detail || 'Failed to send message');
      }
    }
  };

  // D77 — Upload an attachment (image / document / video / voice). The
  // backend deduces message_type from kind or Content-Type. Includes the
  // current replyTo and any composer text as the caption.
  const uploadAttachment = async (file, kind = '') => {
    if (!file || !activeChannel || !wsId) return;
    const MAX = 100 * 1024 * 1024;
    if (file.size > MAX) {
      toast.error(`File too large — max 100 MB (got ${Math.round(file.size / 1024 / 1024)} MB)`);
      return;
    }
    const fd = new FormData();
    fd.append('file', file, file.name || 'attachment');
    if (kind) fd.append('kind', kind);
    if (msgText.trim()) fd.append('text', msgText.trim());
    if (replyTo?.id) fd.append('reply_to_message_id', replyTo.id);
    setUploadingAttachment(true);
    try {
      const res = await chatAPI.uploadAttachment(wsId, activeChannel.id, fd);
      setMessages(prev => [...prev, res.data]);
      setMsgText('');
      setReplyTo(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingAttachment(false);
    }
  };

  // D77 — Per-message delete. mode = 'me' | 'everyone'.
  const deleteMessage = async (msg, mode) => {
    try {
      await chatAPI.deleteMessage(wsId, activeChannel.id, msg.id, mode);
      if (mode === 'me') {
        setMessages(prev => prev.filter(m => m.id !== msg.id));
      } else {
        setMessages(prev => prev.map(m => m.id === msg.id ? {
          ...m, deleted_for_all: true, text: '', text_resolved: '',
          file_url: '', file_name: '', reply_preview: null,
        } : m));
      }
      toast.success(mode === 'me' ? 'Message hidden for you' : 'Message deleted for everyone');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleteTarget(null);
    }
  };

  // D77 — Voice note recording via MediaRecorder.
  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recorderChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        try {
          const blob = new Blob(recorderChunksRef.current, { type: mime || 'audio/webm' });
          const ext = mime.includes('webm') ? 'webm' : 'wav';
          const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: blob.type });
          await uploadAttachment(file, 'voice');
        } finally {
          stream.getTracks().forEach(t => t.stop());
        }
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch (err) {
      toast.error(err?.message || 'Microphone access denied');
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  // Phase 3A Live Fix #1 — composer permission for the current channel.
  // Mirrors the server's _user_can_post_in_channel rule. When false, the
  // composer renders disabled with a read-only hint (not hidden — explicit
  // gating is visible).
  const canPostInActive = useMemo(() => {
    if (!activeChannel) return true;
    const allow = activeChannel.allowed_post_roles || [];
    if (allow.length === 0) return true;
    if (user?.role === 'super_admin') return true;
    return allow.includes(workspaceRole);
  }, [activeChannel, workspaceRole, user]);

  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        wsRef.current?.send(JSON.stringify({ type: 'typing' }));
      }, 300);
    }
  };

  const createChannel = async () => {
    if (!wsId) return;
    if (newChatType === 'group' && !newChatName.trim()) { toast.error('Group name required'); return; }
    if (selectedMembers.length === 0) { toast.error('Select at least one member'); return; }
    try {
      const res = await chatAPI.createChannel(wsId, {
        workspace_id: wsId,
        type: newChatType,
        name: newChatName.trim(),
        member_ids: selectedMembers,
      });
      setShowNewChat(false);
      setNewChatName('');
      setSelectedMembers([]);
      // Fetch channels and set the new one as active
      try {
        const chRes = await chatAPI.channels(wsId);
        setChannels(chRes.data);
        const found = chRes.data.find(ch => ch.id === res.data.id);
        setActiveChannel(found || res.data);
      } catch {
        setActiveChannel(res.data);
      }
      setShowMobile(true);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to create chat'); }
  };

  // Phase 3A Follow-up — create a named channel (public OR private).
  const createNamedChannel = async () => {
    if (!wsId) return;
    const name = (newChannelName || '').trim().toLowerCase();
    if (!CHANNEL_NAME_RE.test(name)) {
      toast.error('Channel name must be 2-32 chars: lowercase letters, numbers, dashes');
      return;
    }
    if (newChannelType === 'private' && newChannelMembers.length === 0) {
      toast.error('Private channels need at least one other member');
      return;
    }
    setNewChannelSubmitting(true);
    try {
      const res = await chatAPI.createChannel(wsId, {
        workspace_id: wsId,
        type: newChannelType,
        name,
        description: (newChannelDesc || '').trim().slice(0, 200),
        member_ids: newChannelMembers,
      });
      setShowNewChannel(false);
      setNewChannelName('');
      setNewChannelDesc('');
      setNewChannelType('public');
      setNewChannelMembers([]);
      // Refresh + auto-select
      try {
        const chRes = await chatAPI.channels(wsId);
        setChannels(chRes.data);
        const found = chRes.data.find((ch) => ch.id === res.data.id);
        setActiveChannel(found || res.data);
      } catch {
        setActiveChannel(res.data);
      }
      setShowMobile(true);
      toast.success(`Created #${name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create channel');
    } finally {
      setNewChannelSubmitting(false);
    }
  };

  // Phase 3A Follow-up — Computed @-mention candidates for the autocomplete
  // popup. Filters channel members by name/email prefix (case-insensitive)
  // and conditionally appends @channel / @here. Excludes the current user.
  const mentionCandidates = useMemo(() => {
    if (!mentionQuery || !activeChannel) return [];
    const q = (mentionQuery.query || '').toLowerCase();
    const channelMemberIds = new Set(activeChannel.members || []);
    const userMatches = members
      .filter((m) => m && m.id !== user?.id && channelMemberIds.has(m.id))
      .filter((m) => {
        if (!q) return true;
        const n = (m.name || '').toLowerCase();
        const e = (m.email || '').toLowerCase().split('@')[0];
        return n.startsWith(q) || e.startsWith(q) || n.includes(q);
      })
      .slice(0, 6)
      .map((m) => ({ kind: 'user', id: m.id, name: m.name, email: m.email }));
    const broadcasts = [];
    if (!q || 'channel'.startsWith(q)) broadcasts.push({ kind: 'channel' });
    if (!q || 'here'.startsWith(q)) broadcasts.push({ kind: 'here' });
    return [...userMatches, ...broadcasts];
  }, [mentionQuery, members, user, activeChannel]);

  // Phase 3A Follow-up — apply a selected mention into the composer text,
  // replacing the partial `@token` with either `<@uuid> ` (user) or the
  // literal `@channel `/`@here ` (broadcast). draftMentionIds tracks user
  // ids so sendMessage can ship a structured payload to the server.
  const applyMention = useCallback((cand) => {
    if (!cand || !mentionQuery) return;
    const { start } = mentionQuery;
    const caret = composerRef.current?.selectionStart ?? msgText.length;
    const before = msgText.slice(0, start);
    const after = msgText.slice(caret);
    let token;
    if (cand.kind === 'user') {
      token = `<@${cand.id}> `;
      setDraftMentionIds((prev) => Array.from(new Set([...prev, cand.id])));
    } else {
      token = `@${cand.kind} `;
    }
    const next = before + token + after;
    setMsgText(next);
    setMentionQuery(null);
    requestAnimationFrame(() => {
      const el = composerRef.current;
      if (el) {
        const pos = (before + token).length;
        try { el.setSelectionRange(pos, pos); } catch (_) {}
        el.focus();
      }
    });
  }, [mentionQuery, msgText]);


  const deleteChannel = async (chId) => {
    try {
      await chatAPI.deleteChannel(wsId, chId);
      if (activeChannel?.id === chId) { setActiveChannel(null); setMessages([]); }
      fetchChannels();
      toast.success('Chat deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const filteredChannels = channels.filter(ch =>
    !channelSearch || ch.display_name?.toLowerCase().includes(channelSearch.toLowerCase())
  );

  const filteredMembers = members.filter(m =>
    m.id !== user?.id && (!memberSearch || m.name?.toLowerCase().includes(memberSearch.toLowerCase()) || m.email?.toLowerCase().includes(memberSearch.toLowerCase()))
  );

  const typingNames = Object.values(typingUsers);
  const grouped = groupMessagesByDate(messages);
  const totalUnread = channels.reduce((sum, ch) => sum + (ch.unread_count || 0), 0);

  return (
    <div className="flex h-full bg-background" data-testid="team-chat-page">
      {/* Channel List */}
      <div className={`w-[280px] border-r border-border/30 flex flex-col shrink-0 ${showMobile && activeChannel ? 'hidden md:flex' : 'flex'} ${!activeChannel ? 'flex-1 md:flex-none' : ''}`}>
        {/* Header */}
        <div className="h-12 flex items-center justify-between px-4 border-b border-border/30 shrink-0">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-gold" />
            <span className="text-[13px] font-semibold">Messages</span>
            {totalUnread > 0 && (
              <span className="text-[10px] bg-gold/20 text-gold px-1.5 py-0.5 rounded-full font-medium" data-testid="total-unread-badge">{totalUnread}</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* Phase 3A Follow-up — Owner/Admin only: dedicated "+ New Channel"
                action, distinct from "+ New Conversation" (which creates DMs/groups). */}
            {isElevated && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setShowNewChannel(true)}
                title="New channel"
                data-testid="new-channel-btn"
              >
                <Hash className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowNewChat(true)} data-testid="new-chat-btn" title="New conversation">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="px-3 py-2">
          <div className="flex items-center gap-2 h-[32px] px-2.5 rounded-md border border-border/20 focus-within:border-gold/30 transition-colors">
            <Search className="h-3.5 w-3.5 text-muted-foreground/30 shrink-0" />
            <input
              value={channelSearch}
              onChange={e => setChannelSearch(e.target.value)}
              placeholder="Search conversations..."
              className="bg-transparent border-none outline-none text-[12px] flex-1 placeholder:text-muted-foreground/25"
              data-testid="chat-search"
            />
          </div>
        </div>

        {/* Channel list */}
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/30" />
            </div>
          ) : filteredChannels.length === 0 ? (
            <div className="text-center py-12 px-4">
              <MessageSquare className="h-8 w-8 text-muted-foreground/10 mx-auto mb-3" />
              <p className="text-[12px] text-muted-foreground/40">No conversations yet</p>
              <Button variant="outline" size="sm" className="mt-3 text-[11px] h-7 border-border/30" onClick={() => setShowNewChat(true)}>
                Start a conversation
              </Button>
            </div>
          ) : (
            <div className="px-1.5 py-1">
              {filteredChannels.map(ch => {
                const isActive = activeChannel?.id === ch.id;
                const isOnline = ch.type === 'dm' && ch.members_info?.some(m => m.id !== user?.id && onlineUsers.has(m.id));
                const isPublic = ch.type === 'public';
                const isPrivate = ch.type === 'private';
                const isAnnouncements = ch.slug === 'announcements';
                // Phase 3A Follow-up — Owner-only "..." menu on non-default channels.
                const canDelete = isOwner && !ch.is_default && ch.type !== 'dm';
                return (
                  <div
                    key={ch.id}
                    className={`group relative flex items-center gap-2.5 w-full pr-1 rounded-lg transition-colors ${
                      isActive ? 'bg-foreground/[0.05]' : 'hover:bg-foreground/[0.02]'
                    }`}
                  >
                    <button
                      onClick={() => { setActiveChannel(ch); setShowMobile(true); }}
                      className="flex items-center gap-2.5 flex-1 px-2.5 py-2 text-left min-w-0"
                      data-testid={`channel-${ch.id}`}
                    >
                      <div className="relative shrink-0">
                        {ch.type === 'dm' ? (
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="text-[10px] bg-secondary">{getInitials(ch.display_name)}</AvatarFallback>
                          </Avatar>
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-gold/10 flex items-center justify-center">
                            {isAnnouncements ? (
                              <Megaphone className="h-3.5 w-3.5 text-gold/70" />
                            ) : isPrivate ? (
                              <Lock className="h-3.5 w-3.5 text-gold/60" />
                            ) : isPublic ? (
                              <Hash className="h-3.5 w-3.5 text-gold/60" />
                            ) : (
                              <Users className="h-3.5 w-3.5 text-gold/60" />
                            )}
                          </div>
                        )}
                        {isOnline && <Circle className="h-2.5 w-2.5 fill-green-500 text-green-500 absolute -bottom-0.5 -right-0.5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className={`text-[12px] truncate ${ch.unread_count > 0 ? 'font-semibold' : ''}`}>{ch.display_name}</span>
                          {ch.last_message?.timestamp && (
                            <span className="text-[9px] text-muted-foreground/30 shrink-0 ml-1">{formatTime(ch.last_message.timestamp)}</span>
                          )}
                        </div>
                        {ch.last_message && (
                          <p className={`text-[11px] truncate mt-0.5 ${ch.unread_count > 0 ? 'text-foreground/70' : 'text-muted-foreground/40'}`}>
                            {ch.last_message.sender_name?.split(' ')[0]}: {ch.last_message.text}
                          </p>
                        )}
                      </div>
                      {ch.unread_count > 0 && (
                        <span className="h-5 min-w-[20px] rounded-full bg-gold/20 text-gold text-[10px] font-medium flex items-center justify-center px-1">{ch.unread_count}</span>
                      )}
                    </button>
                    {canDelete && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-foreground/[0.05] mr-1"
                            data-testid={`channel-menu-${ch.id}`}
                            aria-label="Channel actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem
                            className="text-red-400 focus:text-red-400 focus:bg-red-500/10 text-[12px]"
                            onClick={() => setDeleteChannelTarget(ch)}
                            data-testid={`channel-delete-${ch.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-2" />
                            Delete channel
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Message Area */}
      <div className={`flex-1 flex flex-col min-w-0 ${showMobile && activeChannel ? 'flex' : 'hidden md:flex'}`}>
        {activeChannel ? (
          <>
            {/* Chat header */}
            <div className="h-12 flex items-center justify-between px-4 border-b border-border/30 shrink-0">
              <div className="flex items-center gap-3">
                <button className="md:hidden p-1 rounded hover:bg-foreground/[0.05]" onClick={() => setShowMobile(false)}>
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {activeChannel.type === 'dm' ? (
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="text-[9px] bg-secondary">{getInitials(activeChannel.display_name)}</AvatarFallback>
                  </Avatar>
                ) : (
                  <div className="h-7 w-7 rounded-full bg-gold/10 flex items-center justify-center">
                    <Hash className="h-3.5 w-3.5 text-gold/60" />
                  </div>
                )}
                <div>
                  <p className="text-[13px] font-medium">{activeChannel.display_name}</p>
                  <p className="text-[10px] text-muted-foreground/40">
                    {activeChannel.type === 'group'
                      ? `${activeChannel.members_info?.length || 0} members`
                      : onlineUsers.has(activeChannel.members_info?.find(m => m.id !== user?.id)?.id) ? 'Online' : 'Offline'}
                  </p>
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => deleteChannel(activeChannel.id)} className="text-destructive">
                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete conversation
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {msgLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/30" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16" data-testid="channel-empty-state">
                  <MessageSquare className="h-10 w-10 text-muted-foreground/10 mb-3" />
                  <p className="text-[13px] text-muted-foreground/30">No messages yet</p>
                  {/* Phase 3A Follow-up — empty channel state names the channel. */}
                  <p className="text-[11px] text-muted-foreground/20 mt-1">
                    {activeChannel?.type === 'public' || activeChannel?.type === 'private'
                      ? <>Be the first to post in <span className="text-muted-foreground/40">#{activeChannel.slug || activeChannel.name}</span>.</>
                      : 'Send the first message to start the conversation'}
                  </p>
                </div>
              ) : (
                <div className="space-y-0.5 max-w-3xl mx-auto">
                  {grouped.map((item, idx) => {
                    if (item.type === 'date') {
                      return (
                        <div key={`date-${idx}`} className="flex items-center gap-3 py-4">
                          <div className="flex-1 h-px bg-border/20" />
                          <span className="text-[10px] text-muted-foreground/30 font-medium">{item.label}</span>
                          <div className="flex-1 h-px bg-border/20" />
                        </div>
                      );
                    }
                    const msg = item.data;
                    const isMe = msg.sender_id === user?.id;
                    // Highlight the bubble if THIS viewer is structurally mentioned.
                    const selfMentioned =
                      Array.isArray(msg.mentions) && msg.mentions.includes(user?.id);
                    // D77 — tombstone for messages deleted for everyone.
                    if (msg.deleted_for_all) {
                      return (
                        <div
                          key={msg.id}
                          className={`flex gap-2.5 py-1 ${isMe ? 'flex-row-reverse' : ''}`}
                          data-testid={`msg-${msg.id}`}
                        >
                          {!isMe && (
                            <Avatar className="h-7 w-7 shrink-0 mt-0.5">
                              <AvatarFallback className="text-[9px] bg-secondary">
                                {getInitials(msg.sender_name)}
                              </AvatarFallback>
                            </Avatar>
                          )}
                          <div className={`max-w-[75%] ${isMe ? 'text-right' : ''}`}>
                            {!isMe && <p className="text-[10px] text-muted-foreground/40 mb-0.5 ml-1">{msg.sender_name}</p>}
                            <div
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] italic text-muted-foreground/60 bg-foreground/[0.03] border border-dashed border-border/40"
                              data-testid={`msg-tombstone-${msg.id}`}
                            >
                              <Trash2 className="h-3 w-3" />
                              This message was deleted
                            </div>
                            <p className="text-[9px] text-muted-foreground/20 mt-0.5 mx-1">
                              {formatTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={msg.id}
                        className={`group flex gap-2.5 py-1 ${isMe ? 'flex-row-reverse' : ''}`}
                        data-testid={`msg-${msg.id}`}
                      >
                        {!isMe && (
                          <Avatar className="h-7 w-7 shrink-0 mt-0.5">
                            <AvatarFallback className="text-[9px] bg-secondary">{getInitials(msg.sender_name)}</AvatarFallback>
                          </Avatar>
                        )}
                        <div className={`max-w-[75%] ${isMe ? 'text-right' : ''}`}>
                          {!isMe && <p className="text-[10px] text-muted-foreground/40 mb-0.5 ml-1">{msg.sender_name}</p>}
                          <div className={`inline-block px-3 py-2 rounded-xl text-[12.5px] leading-relaxed whitespace-pre-wrap text-left relative ${
                            selfMentioned
                              ? 'bg-amber-500/10 ring-1 ring-amber-400/30 rounded-tl-sm'
                              : isMe
                                ? 'bg-gold/15 text-foreground rounded-tr-sm'
                                : 'bg-foreground/[0.04] rounded-tl-sm'
                          }`}>
                            {/* D77 — quoted-reply snippet above the body */}
                            <ReplyPreview preview={msg.reply_preview} />
                            {msg.text && <MessageBody text={msg.text} usersById={usersById} selfId={user?.id} />}
                            {/* D77 — media attachment */}
                            {msg.file_url && (
                              <MessageAttachment msg={msg} workspaceId={wsId} channelId={activeChannel?.id} />
                            )}
                          </div>
                          {/* D77b — reaction pills below bubble */}
                          {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                            <div
                              className={`flex flex-wrap gap-1 mt-1 ${isMe ? 'justify-end' : 'justify-start'}`}
                              data-testid={`msg-reactions-${msg.id}`}
                            >
                              {Object.entries(msg.reactions).map(([emoji, uids]) => {
                                const arr = Array.isArray(uids) ? uids : [];
                                if (arr.length === 0) return null;
                                const mineToo = arr.includes(user?.id);
                                return (
                                  <button
                                    key={emoji}
                                    type="button"
                                    onClick={() => chatAPI.reactMessage(wsId, activeChannel.id, msg.id, emoji)
                                      .then((res) => setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, reactions: res.data.reactions } : m)))
                                      .catch(() => {})}
                                    className={`inline-flex items-center gap-0.5 text-[10.5px] px-1.5 py-0.5 rounded-full border ${mineToo ? 'bg-gold/15 border-gold/30 text-gold' : 'bg-foreground/[0.04] border-border/40 text-muted-foreground/80'}`}
                                    data-testid={`msg-reaction-${msg.id}-${emoji}`}
                                  >
                                    <span>{emoji}</span>
                                    <span>{arr.length}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          <p className="text-[9px] text-muted-foreground/20 mt-0.5 mx-1">{formatTime(msg.created_at)}</p>
                        </div>
                        {/* D77 — per-message actions menu, hover-revealed */}
                        <div className={`opacity-0 group-hover:opacity-100 transition-opacity self-center ${isMe ? 'mr-1' : 'ml-1'}`}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-foreground/[0.06]"
                                data-testid={`msg-actions-${msg.id}`}
                              >
                                <MoreVertical className="h-3.5 w-3.5 text-muted-foreground/70" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align={isMe ? 'end' : 'start'} className="text-[12px]">
                              {/* D77b — quick reactions row at the top */}
                              <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border/30">
                                {['👍', '❤️', '😂', '🎉', '😮', '🙏'].map(emo => (
                                  <button
                                    key={emo}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      chatAPI.reactMessage(wsId, activeChannel.id, msg.id, emo)
                                        .then((res) => setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, reactions: res.data.reactions } : m)))
                                        .catch(() => {});
                                    }}
                                    className="h-7 w-7 rounded hover:bg-foreground/[0.06] text-[14px]"
                                    data-testid={`msg-react-quick-${msg.id}-${emo}`}
                                  >
                                    {emo}
                                  </button>
                                ))}
                              </div>
                              <DropdownMenuItem
                                onClick={() => {
                                  setReplyTo({
                                    id: msg.id,
                                    sender_name: msg.sender_name,
                                    text: msg.text || (msg.message_type === 'voice' ? '🎤 Voice message' : msg.file_name || '…'),
                                    message_type: msg.message_type || 'text',
                                  });
                                  composerRef.current?.focus();
                                }}
                                data-testid={`msg-action-reply-${msg.id}`}
                              >
                                <Reply className="h-3 w-3 mr-2" /> Reply
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setDeleteTarget(msg)}
                                className="text-destructive"
                                data-testid={`msg-action-delete-${msg.id}`}
                              >
                                <Trash2 className="h-3 w-3 mr-2" /> Delete…
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
              {/* Typing indicator */}
              {typingNames.length > 0 && (
                <div className="flex items-center gap-2 px-2 py-1 max-w-3xl mx-auto">
                  <div className="flex gap-0.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/30 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/30 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/30 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-[10px] text-muted-foreground/30">{typingNames.join(', ')} typing...</span>
                </div>
              )}
            </ScrollArea>

            {/* Input */}
            <div className="px-4 py-3 border-t border-border/30 shrink-0">
              {/* D77 — Reply-target pill (clears on send or × click) */}
              {replyTo && (
                <div
                  className="max-w-3xl mx-auto mb-2 flex items-start gap-2 px-3 py-2 rounded-md bg-foreground/[0.04] border-l-2 border-gold/50"
                  data-testid="composer-reply-pill"
                >
                  <Reply className="h-3.5 w-3.5 text-gold/70 mt-0.5" />
                  <div className="flex-1 min-w-0 text-[11px]">
                    <div className="font-medium text-gold/80 truncate">Replying to {replyTo.sender_name}</div>
                    <div className="text-muted-foreground/70 truncate">{replyTo.text}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setReplyTo(null)}
                    className="text-muted-foreground/60 hover:text-foreground"
                    data-testid="composer-reply-cancel"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              {/* Composer mention pill strip — Batch 5 R4 C-block.
                  Renders a styled chip per `<@uuid>` token already in the
                  draft so users see whom they're tagging at a glance.
                  Clicking × removes the token from msgText + draftMentionIds.
                  Pills mirror the message-side colour scheme (teal for
                  others, amber for self, violet for @channel/@here). */}
              {(() => {
                const userTokens = Array.from(msgText.matchAll(/<@([a-f0-9-]{36})>/g)).map(m => m[0]);
                const broadcastTokens = Array.from(msgText.matchAll(/@channel\b|@here\b/g)).map(m => m[0]);
                if (userTokens.length === 0 && broadcastTokens.length === 0) return null;
                const removeToken = (tok) => {
                  // Remove the first occurrence + any trailing space; drop the
                  // uuid from draftMentionIds if applicable.
                  const idx = msgText.indexOf(tok);
                  if (idx < 0) return;
                  const nextStr = msgText.slice(0, idx) + msgText.slice(idx + tok.length).replace(/^\s/, '');
                  setMsgText(nextStr);
                  const userMatch = /^<@([a-f0-9-]{36})>$/.exec(tok);
                  if (userMatch) {
                    const uid = userMatch[1];
                    if (!nextStr.includes(`<@${uid}>`)) {
                      setDraftMentionIds(prev => prev.filter(x => x !== uid));
                    }
                  }
                };
                return (
                  <div className="flex flex-wrap items-center gap-1.5 max-w-3xl mx-auto mb-2" data-testid="composer-mention-strip">
                    {userTokens.map((tok, i) => {
                      const m = /^<@([a-f0-9-]{36})>$/.exec(tok);
                      const uid = m ? m[1] : null;
                      const u = uid ? usersById.get(uid) : null;
                      const name = u?.name || u?.email?.split('@')[0] || 'user';
                      const isSelf = uid === user?.id;
                      const cls = isSelf
                        ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                        : 'bg-teal-500/15 text-teal-300 border-teal-500/30';
                      return (
                        <span
                          key={`u-${i}-${tok}`}
                          className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-[11px] font-medium border ${cls}`}
                          data-testid={`composer-mention-pill-${isSelf ? 'self' : 'user'}`}
                          data-mention-uid={uid || ''}
                        >
                          @{name}
                          <button
                            type="button"
                            onClick={() => removeToken(tok)}
                            className="h-3.5 w-3.5 inline-flex items-center justify-center rounded-full hover:bg-foreground/[0.12]"
                            aria-label={`Remove mention ${name}`}
                          >
                            <X className="h-2.5 w-2.5" />
                          </button>
                        </span>
                      );
                    })}
                    {broadcastTokens.map((tok, i) => (
                      <span
                        key={`b-${i}-${tok}`}
                        className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-[11px] font-medium border bg-violet-500/15 text-violet-300 border-violet-500/30"
                        data-testid={`composer-mention-pill-${tok.slice(1)}`}
                      >
                        {tok}
                        <button
                          type="button"
                          onClick={() => removeToken(tok)}
                          className="h-3.5 w-3.5 inline-flex items-center justify-center rounded-full hover:bg-foreground/[0.12]"
                          aria-label={`Remove ${tok}`}
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                );
              })()}
              <div className="flex items-center gap-2 max-w-3xl mx-auto relative">
                <Input
                  ref={composerRef}
                  value={msgText}
                  disabled={!canPostInActive}
                  onChange={e => {
                    if (!canPostInActive) return;
                    const v = e.target.value;
                    setMsgText(v);
                    handleTyping();
                    // Phase 3A Follow-up — open @-autocomplete when the user
                    // is typing an unfinished mention token at the caret.
                    const caret = e.target.selectionStart ?? v.length;
                    const upto = v.slice(0, caret);
                    const at = upto.lastIndexOf('@');
                    if (at >= 0) {
                      const after = upto.slice(at + 1);
                      // Don't trigger if there is whitespace between @ and caret.
                      if (after.length <= 24 && !/\s/.test(after) && !after.includes('@')) {
                        setMentionQuery({ start: at, query: after.toLowerCase() });
                        setMentionSelectedIdx(0);
                        return;
                      }
                    }
                    setMentionQuery(null);
                  }}
                  onKeyDown={e => {
                    if (!canPostInActive) return;
                    // Phase 3A Follow-up — autocomplete keyboard navigation.
                    if (mentionQuery && mentionCandidates.length > 0) {
                      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionSelectedIdx(i => (i + 1) % mentionCandidates.length); return; }
                      if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionSelectedIdx(i => (i - 1 + mentionCandidates.length) % mentionCandidates.length); return; }
                      if (e.key === 'Enter' || e.key === 'Tab') {
                        e.preventDefault();
                        applyMention(mentionCandidates[mentionSelectedIdx]);
                        return;
                      }
                      if (e.key === 'Escape') { e.preventDefault(); setMentionQuery(null); return; }
                    }
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                  }}
                  placeholder={canPostInActive ? "Type a message... use @ to mention" : "Read-only — only Owner, Admin, and Manager can post here."}
                  className={`flex-1 h-9 text-[12.5px] bg-foreground/[0.02] border-border/20 focus:border-gold/30 ${!canPostInActive ? 'opacity-60 cursor-not-allowed' : ''}`}
                  data-testid="chat-message-input"
                />
                {/* Phase 3A Follow-up — @-autocomplete popup. Positioned
                    above the composer; arrow keys + Enter/Tab to select. */}
                {mentionQuery && mentionCandidates.length > 0 && (
                  <div
                    role="listbox"
                    aria-label="Mention autocomplete"
                    className="absolute bottom-12 left-0 z-30 bg-card border border-border/40 rounded-lg shadow-xl py-1 min-w-[200px] max-h-60 overflow-y-auto"
                    data-testid="mention-autocomplete"
                  >
                    {mentionCandidates.map((c, i) => (
                      <button
                        key={c.kind === 'user' ? c.id : c.kind}
                        type="button"
                        role="option"
                        aria-selected={i === mentionSelectedIdx}
                        onClick={() => applyMention(c)}
                        onMouseEnter={() => setMentionSelectedIdx(i)}
                        className={`flex items-center gap-2 w-full px-3 py-1.5 text-[12px] text-left ${
                          i === mentionSelectedIdx ? 'bg-foreground/[0.06]' : 'hover:bg-foreground/[0.03]'
                        }`}
                        data-testid={`mention-option-${c.kind === 'user' ? c.id : c.kind}`}
                        data-mention-index={i}
                      >
                        {c.kind === 'user' ? (
                          <>
                            <Avatar className="h-5 w-5"><AvatarFallback className="text-[9px] bg-secondary">{getInitials(c.name || c.email)}</AvatarFallback></Avatar>
                            <span className="text-foreground/80">{c.name || c.email}</span>
                            <span className="text-muted-foreground/40 text-[10px] ml-auto">{c.email}</span>
                          </>
                        ) : (
                          <>
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-semibold bg-violet-500/20 text-violet-200">@{c.kind}</span>
                            <span className="text-muted-foreground/60 text-[11px]">
                              {c.kind === 'channel' ? 'Notify everyone in the channel' : 'Notify online members only'}
                            </span>
                          </>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                <Button size="sm" className="h-9 w-9 p-0 bg-gold/10 hover:bg-gold/20 text-gold border-0" onClick={sendMessage} disabled={!msgText.trim() || !canPostInActive} data-testid="send-message-btn">
                  <Send className="h-4 w-4" />
                </Button>
                {/* D77 — paperclip (file picker) + microphone (voice note). Hidden when channel is read-only. */}
                {canPostInActive && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      accept="image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/zip,text/*"
                      data-testid="chat-file-input"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadAttachment(f);
                        e.target.value = '';
                      }}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-9 w-9 p-0"
                      disabled={uploadingAttachment || recording}
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="chat-attach-btn"
                      title="Attach photo, video, or file"
                    >
                      {uploadingAttachment ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className={`h-9 w-9 p-0 ${recording ? 'text-red-500' : ''}`}
                      disabled={uploadingAttachment}
                      onClick={() => (recording ? stopVoiceRecording() : startVoiceRecording())}
                      data-testid="chat-voice-btn"
                      title={recording ? 'Stop recording' : 'Record voice note'}
                    >
                      {recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="h-12 w-12 text-muted-foreground/10 mx-auto mb-4" />
              <p className="text-[14px] text-muted-foreground/30 font-medium">Select a conversation</p>
              <p className="text-[12px] text-muted-foreground/20 mt-1">Choose from the sidebar or start a new chat</p>
            </div>
          </div>
        )}
      </div>


      {/* Phase 3A Follow-up — New Channel Dialog (Owner/Admin only).
          Distinct from New Conversation (DMs/groups). Validates the slug
          client-side against the same regex the backend enforces. */}
      {/* D77 — Delete-message confirmation. Sender + admin see "for everyone"; others only get "for me". */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-[420px] bg-card border-border/50" data-testid="chat-delete-modal">
          <DialogHeader>
            <DialogTitle className="text-[15px]">Delete message</DialogTitle>
            <DialogDescription className="text-[11px] text-muted-foreground/60">
              Choose how to delete this message.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-1">
            <button
              type="button"
              className="w-full text-left px-3 py-2.5 rounded-md border border-border/40 hover:bg-foreground/[0.04]"
              onClick={() => deleteTarget && deleteMessage(deleteTarget, 'me')}
              data-testid="chat-delete-for-me"
            >
              <div className="text-[12.5px] font-medium">Delete for me</div>
              <div className="text-[10px] text-muted-foreground/60">Removes the message from your view only.</div>
            </button>
            {deleteTarget && (deleteTarget.sender_id === user?.id || isElevated) && (
              <button
                type="button"
                className="w-full text-left px-3 py-2.5 rounded-md border border-red-700/30 bg-red-900/10 hover:bg-red-900/20"
                onClick={() => deleteTarget && deleteMessage(deleteTarget, 'everyone')}
                data-testid="chat-delete-for-everyone"
              >
                <div className="text-[12.5px] font-medium text-red-300">Delete for everyone</div>
                <div className="text-[10px] text-muted-foreground/60">
                  Removes the message for all participants. A "this message was deleted" placeholder remains in its place.
                </div>
              </button>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(null)} data-testid="chat-delete-cancel">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={showNewChannel} onOpenChange={(o) => { setShowNewChannel(o); if (!o) { setNewChannelName(''); setNewChannelDesc(''); setNewChannelType('public'); setNewChannelMembers([]); } }}>
        <DialogContent className="sm:max-w-[460px] bg-card border-border/50" data-testid="new-channel-dialog">
          <DialogHeader>
            <DialogTitle className="text-[15px]">Create channel</DialogTitle>
            <DialogDescription className="text-[11px] text-muted-foreground/40">
              Public channels auto-add every team member. Private channels are invite-only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-[11px] text-muted-foreground/70 block mb-1">Name</label>
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground/40 text-[13px]">#</span>
                <Input
                  value={newChannelName}
                  onChange={(e) => setNewChannelName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="qa-test-channel"
                  maxLength={32}
                  className="h-9 text-[12.5px]"
                  data-testid="new-channel-name-input"
                  autoFocus
                />
              </div>
              <p className="text-[10px] text-muted-foreground/40 mt-1">
                Lowercase letters, numbers, and dashes. 2–32 chars.
              </p>
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground/70 block mb-1">Description (optional)</label>
              <Input
                value={newChannelDesc}
                onChange={(e) => setNewChannelDesc(e.target.value)}
                placeholder="What's this channel about?"
                maxLength={200}
                className="h-9 text-[12.5px]"
                data-testid="new-channel-desc-input"
              />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground/70 block mb-2">Type</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setNewChannelType('public'); setNewChannelMembers([]); }}
                  className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left transition-colors ${newChannelType === 'public' ? 'border-gold/30 bg-gold/5' : 'border-border/20 hover:border-border/40'}`}
                  data-testid="new-channel-type-public"
                >
                  <Hash className={`h-3.5 w-3.5 ${newChannelType === 'public' ? 'text-gold' : 'text-muted-foreground/50'}`} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-[12px] font-medium ${newChannelType === 'public' ? 'text-gold' : ''}`}>Public</div>
                    <div className="text-[10px] text-muted-foreground/50">All team members auto-join</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setNewChannelType('private')}
                  className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left transition-colors ${newChannelType === 'private' ? 'border-gold/30 bg-gold/5' : 'border-border/20 hover:border-border/40'}`}
                  data-testid="new-channel-type-private"
                >
                  <Lock className={`h-3.5 w-3.5 ${newChannelType === 'private' ? 'text-gold' : 'text-muted-foreground/50'}`} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-[12px] font-medium ${newChannelType === 'private' ? 'text-gold' : ''}`}>Private</div>
                    <div className="text-[10px] text-muted-foreground/50">Invite-only — pick members</div>
                  </div>
                </button>
              </div>
            </div>
            {newChannelType === 'private' && (
              <div>
                <label className="text-[11px] text-muted-foreground/70 block mb-1">Members</label>
                <div className="max-h-40 overflow-y-auto border border-border/20 rounded-lg p-1">
                  {members.filter(m => m && m.id !== user?.id).map(m => (
                    <label key={m.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-foreground/[0.03] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newChannelMembers.includes(m.id)}
                        onChange={(e) => setNewChannelMembers(prev => e.target.checked ? [...prev, m.id] : prev.filter(id => id !== m.id))}
                        data-testid={`new-channel-member-${m.id}`}
                        className="accent-gold"
                      />
                      <Avatar className="h-6 w-6"><AvatarFallback className="text-[9px] bg-secondary">{getInitials(m.name)}</AvatarFallback></Avatar>
                      <span className="text-[12px]">{m.name}</span>
                      <span className="text-[10px] text-muted-foreground/40 ml-auto">{m.email}</span>
                    </label>
                  ))}
                  {members.filter(m => m && m.id !== user?.id).length === 0 && (
                    <p className="text-[11px] text-muted-foreground/40 text-center py-3">No other members yet.</p>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewChannel(false)} className="text-[12px]">Cancel</Button>
            <Button
              onClick={createNamedChannel}
              disabled={!CHANNEL_NAME_RE.test((newChannelName || '').trim().toLowerCase()) || newChannelSubmitting || (newChannelType === 'private' && newChannelMembers.length === 0)}
              className="bg-gold/15 hover:bg-gold/25 text-gold text-[12px] border-0"
              data-testid="new-channel-submit-btn"
            >
              {newChannelSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : null}
              Create channel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 3A Follow-up — Delete-channel confirmation. Owner-only flow;
          server-side enforces is_default protection regardless of viewer. */}
      <Dialog open={!!deleteChannelTarget} onOpenChange={(o) => { if (!o) setDeleteChannelTarget(null); }}>
        <DialogContent className="sm:max-w-[400px] bg-card border-border/50" data-testid="delete-channel-dialog">
          <DialogHeader>
            <DialogTitle className="text-[15px]">Delete channel</DialogTitle>
            <DialogDescription className="text-[12px] text-muted-foreground/60">
              {deleteChannelTarget && (
                <>This will permanently delete <span className="text-foreground/80">#{deleteChannelTarget.slug || deleteChannelTarget.name}</span> and all its messages. This action cannot be undone.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteChannelTarget(null)} className="text-[12px]">Cancel</Button>
            <Button
              onClick={async () => {
                if (!deleteChannelTarget) return;
                const id = deleteChannelTarget.id;
                setDeleteChannelTarget(null);
                await deleteChannel(id);
              }}
              className="bg-red-500/15 hover:bg-red-500/25 text-red-300 text-[12px] border-0"
              data-testid="delete-channel-confirm-btn"
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete channel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Chat Dialog */}
      <Dialog open={showNewChat} onOpenChange={setShowNewChat}>
        <DialogContent className="sm:max-w-[420px] bg-card border-border/50" data-testid="new-chat-dialog">
          <DialogHeader>
            <DialogTitle className="text-[15px]">New conversation</DialogTitle>
            <DialogDescription className="text-[11px] text-muted-foreground/40">Start a direct message or create a group chat</DialogDescription>
          </DialogHeader>
          {/* Type selector */}
          <div className="flex gap-2 my-2">
            <button
              onClick={() => { setNewChatType('dm'); setSelectedMembers([]); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-[12px] border transition-colors ${
                newChatType === 'dm' ? 'border-gold/30 bg-gold/5 text-gold' : 'border-border/20 text-muted-foreground hover:border-border/40'
              }`}
              data-testid="chat-type-dm"
            >
              <User className="h-3.5 w-3.5" /> Direct Message
            </button>
            <button
              onClick={() => { setNewChatType('group'); setSelectedMembers([]); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-[12px] border transition-colors ${
                newChatType === 'group' ? 'border-gold/30 bg-gold/5 text-gold' : 'border-border/20 text-muted-foreground hover:border-border/40'
              }`}
              data-testid="chat-type-group"
            >
              <Users className="h-3.5 w-3.5" /> Group Chat
            </button>
          </div>

          {newChatType === 'group' && (
            <Input
              value={newChatName}
              onChange={e => setNewChatName(e.target.value)}
              placeholder="Group name..."
              className="h-8 text-[12px] border-border/20"
              data-testid="group-name-input"
            />
          )}

          {/* Selected members */}
          {selectedMembers.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selectedMembers.map(mid => {
                const m = members.find(u => u.id === mid);
                return (
                  <span key={mid} className="flex items-center gap-1 px-2 py-1 rounded-md bg-gold/10 text-[11px] text-gold">
                    {m?.name || mid}
                    <button onClick={() => setSelectedMembers(prev => prev.filter(id => id !== mid))}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Member search */}
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-muted-foreground/30 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={memberSearch}
              onChange={e => setMemberSearch(e.target.value)}
              placeholder="Search team members..."
              className="h-8 text-[12px] pl-8 border-border/20"
              data-testid="member-search-input"
            />
          </div>

          {/* Member list */}
          <ScrollArea className="max-h-[200px]">
            <div className="space-y-0.5">
              {filteredMembers.map(m => {
                const selected = selectedMembers.includes(m.id);
                return (
                  <button
                    key={m.id}
                    onClick={() => {
                      if (newChatType === 'dm') {
                        setSelectedMembers([m.id]);
                      } else {
                        setSelectedMembers(prev => selected ? prev.filter(id => id !== m.id) : [...prev, m.id]);
                      }
                    }}
                    className={`flex items-center gap-2.5 w-full px-2.5 py-2 rounded-md text-left transition-colors ${
                      selected ? 'bg-gold/5 border border-gold/20' : 'hover:bg-foreground/[0.02] border border-transparent'
                    }`}
                    data-testid={`member-option-${m.id}`}
                  >
                    <div className="relative">
                      <Avatar className="h-7 w-7">
                        <AvatarFallback className="text-[9px] bg-secondary">{getInitials(m.name)}</AvatarFallback>
                      </Avatar>
                      {onlineUsers.has(m.id) && <Circle className="h-2 w-2 fill-green-500 text-green-500 absolute -bottom-0.5 -right-0.5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] truncate">{m.name}</p>
                      <p className="text-[10px] text-muted-foreground/30 truncate">{m.email}</p>
                    </div>
                    {selected && <div className="h-4 w-4 rounded-full bg-gold flex items-center justify-center"><span className="text-[10px] text-black font-bold">&#10003;</span></div>}
                  </button>
                );
              })}
              {filteredMembers.length === 0 && (
                <p className="text-[11px] text-muted-foreground/30 text-center py-4">No team members found</p>
              )}
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowNewChat(false)} className="text-[12px] h-8 border-border/30">Cancel</Button>
            <Button size="sm" onClick={createChannel} disabled={selectedMembers.length === 0} className="text-[12px] h-8 bg-gold/10 hover:bg-gold/20 text-gold border-0" data-testid="create-chat-btn">
              {newChatType === 'dm' ? 'Start Chat' : 'Create Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
